import {
  Box,
  Divider,
  HStack,
  IconButton,
  Stack,
  Text,
  useToast,
  Table,
  Tbody,
  Tr,
  Td,
  Badge,
  Tooltip,
  Button,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  EventDetailConfirmationEmailsSection,
  KYDEventConfirmationEmail,
} from "@common/interfaces/KYDEvent";
import { FaPlus } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import { SectionProps } from "../sections/sectionsComponents";
import { EditModalType } from "../../../utils";
import { useCallback, useState } from "react";
import ConfirmationEmailModal, {
  ConfirmationEmailValue,
} from "./ConfirmationEmailModal";
import { useLoading } from "../../hooks/form";
import actions from "../../../actions";
import { useDialogWithOptions } from "../../../Common/Dialog";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import EventDetailTitle from "../../EventDetailTitle";

const ConfirmationEmailsSection = ({
  data = {
    confirmation_emails: [],
    ticket_types: [],
    currency: undefined,
    id: undefined,
    link: undefined,
    short_link: undefined,
    status: undefined,
    timezone: undefined,
  },
  permissions,
  isLoading,
  refetchEvent,
  setModalType,
  modalType,
}: SectionProps<EventDetailConfirmationEmailsSection["data"]>) => {
  const [editingNote, setEditingNote] =
    useState<KYDEventConfirmationEmail | null>(null);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [loading, updateLoading] = useLoading({
    note: false,
  });
  const toast = useToast();
  const { open: dialog, close: closeDialog } = useDialogWithOptions();

  const onCreateNote = useCallback(
    async (createData: ConfirmationEmailValue) => {
      updateLoading("note", true);
      try {
        if (!data.id) {
          throw Error("NO event ID provided");
        }

        await actions.ADMIN.createConfirmationEmail(data.id, {
          name: createData.name,
          note: createData.note,
          note_rich: createData.note_rich,
          ticket_type_ids: createData.ticket_type_ids,
        });
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        toast({
          title: "Note Created",
          description: "Confirmation email note has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("note", false);
    },
    [updateLoading, refetchEvent, dialog, data.id, setModalType, toast]
  );

  const onUpdateNote = useCallback(
    async (noteData: ConfirmationEmailValue) => {
      updateLoading("note", true);
      try {
        await actions.ADMIN.updateConfirmationEmail(data.id, noteData.id, {
          name: noteData.name,
          note: noteData.note,
          note_rich: noteData.note_rich,
          ticket_type_ids: noteData.ticket_type_ids,
        });
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        toast({
          title: "Note Updated",
          description: "Confirmation email note has been updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("note", false);
    },
    [updateLoading, refetchEvent, dialog, data.id, setModalType, toast]
  );

  const onDeleteNote = useCallback(
    async (noteId: string) => {
      dialog({
        title: "Delete Note",
        text: "Are you sure you want to delete this note? This action cannot be undone.",
        actions: [
          {
            text: "Delete",
            type: "destructive",
            buttonProps: {
              backgroundColor: "red.500",
              color: "white",
              _hover: {
                backgroundColor: "red.600",
              },
            },
            onClick: async () => {
              updateLoading("note", true);
              try {
                await actions.ADMIN.deleteConfirmationEmail(data.id, noteId);
                await refetchEvent({ silent: true });
                toast({
                  title: "Note Deleted",
                  description: "Confirmation email note has been deleted",
                  status: "success",
                  duration: 5000,
                  isClosable: true,
                });
                closeDialog();
              } catch (err) {
                dialog({ text: err.message });
              }
              updateLoading("note", false);
            },
          },
          {
            text: "Cancel",
            type: "cancel",
            buttonProps: {
              backgroundColor: "gray.400",
              color: "white",
              borderColor: "gray.300",
              borderWidth: 1,
              borderRadius: "md",
              _hover: {
                backgroundColor: "gray.500",
              },
            },
            onClick: closeDialog,
          },
        ],
      });
    },
    [dialog, updateLoading, data.id, refetchEvent, toast]
  );

  if (!data) {
    return null;
  }

  const actionButtonStyle = {
    size: "sm",
    variant: "outline",
    padding: "10px 12px",
    lineHeight: "19.2px",
    color: "#1A202C",
    fontFamily: "Work Sans",
    fontSize: "13px",
    fontWeight: "500",
    height: "40px",
    borderRadius: "10px",
    border: "1px solid #E2E2E2",
    background: "#FFF",
    boxShadow: "0 2px 6px 0 rgba(0, 0, 0, 0.05)",
  } as const;

  const deleteIconButtonStyle = {
    "aria-label": "Delete note",
    icon: <FiTrash2 />,
    size: "sm",
    variant: "outline",
    height: "40px",
    width: "40px",
    borderRadius: "10px",
    border: "1px solid #E2E2E2",
    background: "#FFF",
    boxShadow: "0 2px 6px 0 rgba(0, 0, 0, 0.05)",
  } as const;

  const openNoteModal = (
    confirmationEmail: KYDEventConfirmationEmail,
    isPreview: boolean
  ) => {
    setEditingNote(confirmationEmail);
    setModalType(EditModalType.CONFIRMATION_EMAIL);
    setPreviewMode(isPreview);
  };

  const MailIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M28.555 11.1677L16.555 3.16766C16.3907 3.05802 16.1975 2.99951 16 2.99951C15.8025 2.99951 15.6093 3.05802 15.445 3.16766L3.445 11.1677C3.30801 11.2591 3.19572 11.3829 3.11809 11.5281C3.04046 11.6733 2.99989 11.8355 3 12.0002V25.0002C3 25.5306 3.21071 26.0393 3.58579 26.4144C3.96086 26.7894 4.46957 27.0002 5 27.0002H27C27.5304 27.0002 28.0391 26.7894 28.4142 26.4144C28.7893 26.0393 29 25.5306 29 25.0002V12.0002C29.0001 11.8355 28.9595 11.6733 28.8819 11.5281C28.8043 11.3829 28.692 11.2591 28.555 11.1677ZM12.09 19.0002L5 24.0002V13.9414L12.09 19.0002ZM14.1362 20.0002H17.8638L24.9425 25.0002H7.0575L14.1362 20.0002ZM19.91 19.0002L27 13.9414V24.0002L19.91 19.0002ZM16 5.20141L26.2388 12.0277L17.8638 18.0002H14.1388L5.76375 12.0277L16 5.20141Z"
        fill="#718096"
      />
    </svg>
  );

  const renderTicketTypeBadges = (
    confirmationEmail: KYDEventConfirmationEmail
  ) => (
    <Stack direction="row" wrap="wrap" spacing={2}>
      {confirmationEmail.ticket_type_ids.length === 0
        ? null
        : confirmationEmail.ticket_type_ids.slice(0, 3).map((ttId) => {
            const tt = data.ticket_types.find((t) => t.id === ttId);
            if (!tt) {
              return null;
            }
            return (
              <Badge
                key={ttId}
                colorScheme="gray"
                color="#2D3748"
                fontFamily="Work Sans"
                fontSize="12px"
                fontWeight="600"
                lineHeight="100%"
                textTransform={"capitalize"}
                padding="4px 8px"
                borderRadius="md"
              >
                {tt?.name || ttId}
              </Badge>
            );
          })}
      {confirmationEmail.ticket_type_ids.length > 3 && (
        <Badge colorScheme="gray">
          +{confirmationEmail.ticket_type_ids.length - 3} more
        </Badge>
      )}
    </Stack>
  );

  const renderActions = (
    confirmationEmail: KYDEventConfirmationEmail,
    opts?: { withSeparator?: boolean }
  ) => (
    <ShowIfHasPermission permissions={permissions} checkFor="write">
      <HStack
        justify={{ base: "space-between", md: "flex-end" }}
        w="100%"
        pt={2}
      >
        <HStack spacing={3}>
          <Button
            {...actionButtonStyle}
            onClick={() => openNoteModal(confirmationEmail, false)}
          >
            Edit
          </Button>
          <Button
            {...actionButtonStyle}
            onClick={() => openNoteModal(confirmationEmail, true)}
          >
            Preview
          </Button>
        </HStack>

        <HStack spacing={2}>
          {opts?.withSeparator ? (
            <Box
              as="span"
              height="40px"
              width="1px"
              background="#EDF2F7"
              marginX={2}
              display="inline-block"
              verticalAlign="middle"
            />
          ) : null}
          <Tooltip label="Delete">
            <IconButton
              {...deleteIconButtonStyle}
              onClick={() => onDeleteNote(confirmationEmail.id)}
            />
          </Tooltip>
        </HStack>
      </HStack>
    </ShowIfHasPermission>
  );

  return (
    <Stack>
      <ConfirmationEmailModal
        data={data}
        isOpen={modalType === EditModalType.CONFIRMATION_EMAIL}
        isLoading={isLoading || loading.note}
        isPreviewMode={previewMode}
        onClose={() => {
          setEditingNote(null);
          setModalType(EditModalType.INACTIVE);
          setPreviewMode(false);
        }}
        onSave={editingNote ? onUpdateNote : onCreateNote}
        note={editingNote}
      />

      <Box>
        <HStack justify={"space-between"} w="100%">
          <EventDetailTitle>Confirmation Email Notes</EventDetailTitle>
          <ShowIfHasPermission permissions={permissions} checkFor="write">
            <IconButton
              isDisabled={isLoading}
              variant={"outline"}
              aria-label="Add note"
              icon={<FaPlus />}
              onClick={() => {
                setEditingNote(null);
                setModalType(EditModalType.CONFIRMATION_EMAIL);
              }}
            />
          </ShowIfHasPermission>
        </HStack>
        <Box>
          <Text color="gray.500" fontSize="sm" maxW="calc(100% - 60px)">
            Add additional information to order confirmation emails for selected
            ticket types.
          </Text>
        </Box>
        {data.confirmation_emails.length === 0 ? (
          <Text color="gray.500">No confirmation email notes created</Text>
        ) : null}
      </Box>

      <Box>
        {data.confirmation_emails?.length > 0 && isMobile ? (
          <Stack spacing={4}>
            {data.confirmation_emails.map((confirmationEmail, idx) => (
              <Box key={confirmationEmail.id}>
                <Box
                  border="1px solid #EDF2F7"
                  borderRadius="16px"
                  background="#FFF"
                  boxShadow="0 2px 6px 0 rgba(0, 0, 0, 0.05)"
                  padding={4}
                >
                  <HStack align="flex-start" spacing={3}>
                    <Box flexShrink={0} pt={1}>
                      <MailIcon />
                    </Box>

                    <VStack
                      alignItems={"flex-start"}
                      justifyContent={"flex-start"}
                      spacing={2}
                      flex={1}
                      minW={0}
                    >
                      <Text fontWeight={"medium"} noOfLines={2}>
                        {confirmationEmail.name || "Untitled Email"}
                      </Text>
                      {renderTicketTypeBadges(confirmationEmail)}
                      {renderActions(confirmationEmail)}
                    </VStack>
                  </HStack>
                </Box>

                {idx !== data.confirmation_emails.length - 1 ? (
                  <Divider mt={4} />
                ) : null}
              </Box>
            ))}
          </Stack>
        ) : data.confirmation_emails?.length > 0 ? (
          <Table variant="simple">
            <Tbody>
              {data.confirmation_emails.map((confirmationEmail) => (
                <Tr key={confirmationEmail.id}>
                  <Td px={0}>
                    <HStack>
                      <Box>
                        <MailIcon />
                      </Box>
                      <VStack
                        alignItems={"flex-start"}
                        justifyContent={"flex-start"}
                        flex={1}
                        minW={0}
                      >
                        <Text fontWeight={"medium"}>
                          {confirmationEmail.name || "Untitled Email"}
                        </Text>
                        {renderTicketTypeBadges(confirmationEmail)}
                      </VStack>
                    </HStack>
                  </Td>
                  <Td px={0}>
                    <HStack justify="flex-end">
                      {renderActions(confirmationEmail, {
                        withSeparator: true,
                      })}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : null}
      </Box>
    </Stack>
  );
};

export default ConfirmationEmailsSection;
